// src/lib/firebaseStorage.ts
//
// Document uploads for properties land in Firebase Storage at
//   supabase_properties/{propertyId}/documents/{filename}
//
// (The "supabase_" prefix is just a path segment — files live in Firebase
// Storage, not Supabase. The naming reflects how the property-side data
// model is rooted in Supabase tables.)

import { ref, uploadBytesResumable, getDownloadURL } from "@firebase/storage";
import type { UploadMetadata } from "@firebase/storage";
import { firebaseStorage } from "@/shared/integrations/firebase/client";
import {
  gsUriFromDownloadUrl,
  parseFirebaseStorageLocation,
  thumbnailObjectPath,
} from "./firebaseStorageUrl";

export interface UploadResult {
  /** Full storage path (e.g. "supabase_properties/<id>/documents/foo.pdf"). */
  path: string;
  /** Public download URL (httpsURL with signed token). */
  downloadUrl: string;
}

export interface UploadOptions {
  /** Called with a value 0..1 as bytes are transferred. */
  onProgress?: (percent: number) => void;
  /** AbortSignal — calling `.abort()` on the controller cancels the upload. */
  signal?: AbortSignal;
}

/**
 * Upload a single file to the property's documents folder.
 *
 * If a file with the same name already exists at that path, this function
 * silently appends `-<epochMs>` before the extension to avoid overwriting:
 * "report.pdf" → "report-1715900000000.pdf".
 */
export async function uploadPropertyDocument(
  propertyId: string,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  if (!propertyId) throw new Error("uploadPropertyDocument: propertyId is required");
  if (!file) throw new Error("uploadPropertyDocument: file is required");

  const safeName = uniqueName(file.name);
  const path = `supabase_properties/${propertyId}/documents/${safeName}`;
  const storageRef = ref(firebaseStorage, path);

  const task = uploadBytesResumable(storageRef, file, {
    contentType: file.type || "application/octet-stream",
  });

  // Cancel on abort signal
  if (opts.signal) {
    opts.signal.addEventListener("abort", () => task.cancel(), { once: true });
  }

  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        if (opts.onProgress && snap.totalBytes > 0) {
          opts.onProgress(snap.bytesTransferred / snap.totalBytes);
        }
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  const downloadUrl = await getDownloadURL(task.snapshot.ref);
  return { path, downloadUrl };
}

/**
 * Where a survey image belongs in storage. Mirrors the Flutter field app's
 * layout so files from both apps land in the same folder structure:
 *   supabase_properties/<propertyId>/attributes/<surveyId>/<...nodeIds>/photos/<uuid>.<ext>
 *   supabase_properties/<propertyId>/attributes/<surveyId>/<...nodeIds>/evidence/<fieldId>/<uuid>.<ext>
 */
export interface SurveyUploadLocation {
  propertyId: string;
  /** The property_attributes row id (Flutter calls this propertyAttributeId). */
  surveyId: string;
  /** Ids of the ancestor node sections, outermost first. Empty for root-level fields. */
  nodePath: string[];
}

/**
 * Upload a single image to a survey question's evidence folder.
 *
 * The caller is responsible for resizing/compressing the image before
 * passing it in (see resizeImage in src/utils/imageProcessing.ts).
 */
export async function uploadSurveyEvidence(
  location: SurveyUploadLocation,
  fieldId: string,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  if (!fieldId) throw new Error("uploadSurveyEvidence: fieldId is required");
  const path = `${surveyBasePath("uploadSurveyEvidence", location)}/evidence/${fieldId}/${surveyFileName(file)}`;
  return uploadImageToPath(path, file, opts);
}

/**
 * Upload a single image to a survey photos-question's folder.
 *
 * The caller is responsible for resizing/compressing the image before
 * passing it in (see resizeImage in src/utils/imageProcessing.ts).
 */
export async function uploadSurveyPhoto(
  location: SurveyUploadLocation,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  const path = `${surveyBasePath("uploadSurveyPhoto", location)}/photos/${surveyFileName(file)}`;
  return uploadImageToPath(path, file, opts);
}

function surveyBasePath(caller: string, location: SurveyUploadLocation): string {
  const { propertyId, surveyId, nodePath } = location;
  if (!propertyId) throw new Error(`${caller}: propertyId is required`);
  if (!surveyId) throw new Error(`${caller}: surveyId is required`);
  const nodeSegment = nodePath.length > 0 ? `/${nodePath.join("/")}` : "";
  return `supabase_properties/${propertyId}/attributes/${surveyId}${nodeSegment}`;
}

/** Random uuid filename keeping the original extension, matching the Flutter app. */
function surveyFileName(file: File): string {
  const dot = file.name.lastIndexOf(".");
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "jpg";
  return `${crypto.randomUUID()}.${ext}`;
}

/**
 * Overwrite the object at `location` (a path or `gs://` URI) in place.
 *
 * Deliberately sends the same metadata as `uploadImageToPath` — `contentType`
 * and nothing else. An earlier version also set `cacheControl` and pinned the
 * object's previous `firebaseStorageDownloadTokens` so existing URLs would stay
 * valid; the v0 endpoint rejects that with an HTTP 400 the SDK surfaces only as
 * `storage/unknown`. Overwriting therefore mints a *new* download token and the
 * old URL 403s, so callers must persist the returned `downloadUrl`.
 */
export async function overwriteImageAtPath(
  location: string,
  blob: Blob,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  if (!location) throw new Error("overwriteImageAtPath: location is required");
  const metadata: UploadMetadata = { contentType: blob.type || "image/jpeg" };
  return uploadBlobToPath(location, blob, metadata, opts);
}

/**
 * A `gs://` reference to the object a download URL names, or null if the URL
 * isn't a Firebase download URL.
 *
 * Deliberately not the bare path: `ref()` resolves a bare path against the app's
 * configured bucket, which is the wrong object whenever the app is pointed at a
 * different project than the photo lives in.
 */
export function storageLocationFromDownloadUrl(url: string): string | null {
  return gsUriFromDownloadUrl(url);
}

/**
 * Download URL for an image's stored 250 px thumbnail, or null when it has none.
 *
 * The thumbnail is a separate object with its own download token, so the URL has
 * to be minted rather than derived from the original's — one small metadata
 * request, against 2.4 MB of image bytes saved. Callers should cache the result
 * and fall back to the original URL on null.
 */
export async function thumbnailDownloadUrl(url: string): Promise<string | null> {
  const location = parseFirebaseStorageLocation(url);
  if (!location) return null;
  const thumbPath = thumbnailObjectPath(location.path);
  if (!thumbPath) return null;
  try {
    return await getDownloadURL(ref(firebaseStorage, `gs://${location.bucket}/${thumbPath}`));
  } catch {
    // No thumbnail for this object (Flutter-captured asset photos never have
    // one), or it isn't readable. The caller renders the original.
    return null;
  }
}

async function uploadImageToPath(
  path: string,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  if (!file) throw new Error("upload: file is required");
  return uploadBlobToPath(path, file, { contentType: file.type || "image/jpeg" }, opts);
}

async function uploadBlobToPath(
  path: string,
  blob: Blob,
  metadata: UploadMetadata,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  const storageRef = ref(firebaseStorage, path);

  const task = uploadBytesResumable(storageRef, blob, metadata);

  if (opts.signal) {
    opts.signal.addEventListener("abort", () => task.cancel(), { once: true });
  }

  await new Promise<void>((resolve, reject) => {
    task.on(
      "state_changed",
      (snap) => {
        if (opts.onProgress && snap.totalBytes > 0) {
          opts.onProgress(snap.bytesTransferred / snap.totalBytes);
        }
      },
      (err) => reject(err),
      () => resolve(),
    );
  });

  const downloadUrl = await getDownloadURL(task.snapshot.ref);
  return { path, downloadUrl };
}

/**
 * Re-mint a fresh download URL for a Firebase Storage object given its
 * (possibly stale-token) download URL.
 *
 * Firebase download URLs embed a download token that is invalidated when the
 * object is overwritten (a new upload mints a new token) or revoked in the
 * console — old URLs then return HTTP 403 "Permission denied". Calling
 * getDownloadURL reads the object's current metadata and returns a URL with a
 * valid token.
 *
 * Returns null when the URL isn't a Firebase Storage download URL (so callers
 * can pass non-Firebase http URLs through unchanged) or when the object can't
 * be read (e.g. it no longer exists).
 */
export async function refreshFirebaseDownloadUrl(url: string): Promise<string | null> {
  // gs:// rather than the bare path so the re-mint reads the bucket the URL
  // names, not whichever bucket this build happens to be configured for.
  const path = gsUriFromDownloadUrl(url);
  if (!path) return null;
  try {
    return await getDownloadURL(ref(firebaseStorage, path));
  } catch {
    return null;
  }
}

/**
 * Sanitize an asset name for use in storage paths, matching the Flutter
 * field app's safeFileName: special chars → "_", collapsed and trimmed.
 */
export function safeAssetFileName(assetName: string, fallback = "asset"): string {
  const base = assetName.trim();
  if (!base) return fallback;
  return base
    .replace(/[\s/\\:*?"<>|]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_/, "")
    .replace(/_$/, "");
}

/**
 * Upload an asset nameplate photo, matching the Flutter app's layout:
 *   supabase_properties/<propertyId>/assets/<folderId>/<assetName>_nameplate.jpg
 * `folderId` is the scanned QR code id on mobile; web creation has no QR, so
 * callers pass the asset's ref_id (temp id) instead.
 */
export async function uploadAssetNameplatePhoto(
  propertyId: string,
  folderId: string,
  assetName: string,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  const safe = safeAssetFileName(assetName);
  const path = `supabase_properties/${propertyId}/assets/${folderId}/${safe}_nameplate.jpg`;
  return uploadImageToPath(path, file, opts);
}

/**
 * Upload an asset context photo, matching the Flutter app's layout:
 *   supabase_properties/<propertyId>/assets/<folderId>/photos/<assetName>_photo_<n>.jpg
 */
export async function uploadAssetContextPhoto(
  propertyId: string,
  folderId: string,
  assetName: string,
  index: number,
  file: File,
  opts: UploadOptions = {},
): Promise<UploadResult> {
  const safe = safeAssetFileName(assetName);
  const n = Math.max(1, index);
  const path = `supabase_properties/${propertyId}/assets/${folderId}/photos/${safe}_photo_${n}.jpg`;
  return uploadImageToPath(path, file, opts);
}

/**
 * Append the epoch ms before the extension to avoid overwrites at the
 * Storage path. Only suffixes when the name has a "." (extension). We don't
 * probe Storage for existence — silent dedupe is cheaper than a round-trip.
 */
function uniqueName(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return name;
  const base = name.slice(0, dot);
  const ext = name.slice(dot);
  const stamp = Date.now();
  return `${base}-${stamp}${ext}`;
}
